import { useState, useEffect, useCallback } from 'react';
import agentChatService from '../services/agentChatService';

/**
 * React hook for managing chat sessions with Option 3 functionality
 * Provides both "New Conversation" and "Clear Chat" features
 */
export const useChatSession = (initialOptions = {}) => {
  const {
    autoLoadHistory = true,
    onNewConversation = null,
    onClearSession = null,
    onError = null
  } = initialOptions;

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSessionReady, setIsSessionReady] = useState(false);

  // Load chat history for a session
  const loadSessionHistory = useCallback(async (targetSessionId = null) => {
    try {
      setIsLoading(true);
      setError(null);

      const sessionIdToUse = targetSessionId || sessionId;
      if (!sessionIdToUse) return;

      const historyData = await agentChatService.getChatHistory(sessionIdToUse, {
        page: 1,
        pageSize: 100 // Load last 100 messages
      });

      // Transform backend format to frontend format
      const transformedMessages = historyData.messages.map(msg => ({
        id: msg._id || `${msg.type}_${msg.timestamp}`,
        type: msg.type, // 'user' or 'assistant'
        content: msg.message,
        timestamp: new Date(msg.timestamp),
        metadata: msg.metadata || {},
        files: msg.files || []
      }));

      setMessages(transformedMessages);
    } catch (err) {
      console.error('Failed to load session history:', err);
      setError(err.message);
      if (onError) onError(err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, onError]);

  // Initialize session on mount
  useEffect(() => {
    const initializeSession = async () => {
      try {
        setIsLoading(true);
        // Get existing session or create new one if none exists
        const currentSessionId = agentChatService.getSessionId();
        console.log('🔑 Using session ID:', currentSessionId);
        setSessionId(currentSessionId);

        // Auto-load history if enabled
        if (autoLoadHistory) {
          try {
            const historyData = await agentChatService.getChatHistory(currentSessionId, {
              page: 1,
              pageSize: 100
            });

            const transformedMessages = historyData.messages.map(msg => ({
              id: msg._id || `${msg.type}_${msg.timestamp}`,
              type: msg.type,
              content: msg.message,
              timestamp: new Date(msg.timestamp),
              metadata: msg.metadata || {},
              files: msg.files || []
            }));

            setMessages(transformedMessages);
          } catch (historyErr) {
            console.error('Failed to load initial history:', historyErr);
            if (onError) onError(historyErr);
          }
        }

        setIsSessionReady(true);
      } catch (err) {
        console.error('Failed to initialize session:', err);
        setError(err.message);
        if (onError) onError(err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, [autoLoadHistory, onError]); // ✅ Include onError dependency

  // Start a new conversation (Option 3 - clear current + new session)
  const newConversation = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await agentChatService.newConversation();

      // Update state with the new session ID
      setSessionId(result.newSessionId);
      setMessages([]);

      // Add system message to indicate new conversation
      const systemMessage = {
        id: `system_${Date.now()}`,
        type: 'system',
        content: 'Started new conversation',
        timestamp: new Date(),
        metadata: { isSystemMessage: true }
      };
      setMessages([systemMessage]);

      // Callback
      if (onNewConversation) {
        onNewConversation(result);
      }

      return result;
    } catch (err) {
      console.error('Failed to start new conversation:', err);
      setError(err.message);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [onNewConversation, onError]);

  // Clear current session history (keep same session ID)
  const clearSession = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await agentChatService.clearSession(sessionId);

      // Clear messages in UI
      setMessages([]);

      // Add system message to indicate cleared chat
      const systemMessage = {
        id: `system_${Date.now()}`,
        type: 'system',
        content: 'Chat history cleared',
        timestamp: new Date(),
        metadata: { isSystemMessage: true }
      };
      setMessages([systemMessage]);

      // Callback
      if (onClearSession) {
        onClearSession(result);
      }

      return result;
    } catch (err) {
      console.error('Failed to clear session:', err);
      setError(err.message);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, onClearSession, onError]);

  // Send a message
  const sendMessage = useCallback(async (messageContent, options = {}) => {
    if (!sessionId || !isSessionReady) {
      throw new Error('Session not ready');
    }

    try {
      setIsLoading(true);
      setError(null);

      // Add user message to UI immediately
      const userMessage = {
        id: `user_${Date.now()}`,
        type: 'user',
        content: messageContent,
        timestamp: new Date(),
        metadata: options.metadata || {},
        files: options.files || []
      };

      setMessages(prev => [...prev, userMessage]);

      // Send to backend
      console.log('📨 Hook sending to service:', {
        message: messageContent,
        files: options.files?.map(f => ({ name: f.name, size: f.size, type: f.type })) || null,
        metadata: options.metadata
      });

      const response = await agentChatService.sendMessage({
        message: messageContent,
        files: options.files,
        metadata: options.metadata
      });

      // Add assistant response to UI
      const assistantMessage = {
        id: `assistant_${Date.now()}`,
        type: 'assistant',
        content: response.message,
        timestamp: new Date(),
        metadata: response.metadata || {}
      };

      setMessages(prev => [...prev, assistantMessage]);

      return response;
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.message);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isSessionReady, onError]);

  // Stream a message (for real-time responses)
  const streamMessage = useCallback(async (messageContent, onChunk, options = {}) => {
    if (!sessionId || !isSessionReady) {
      throw new Error('Session not ready');
    }

    try {
      setIsLoading(true);
      setError(null);

      // Add user message to UI immediately
      const userMessage = {
        id: `user_${Date.now()}`,
        type: 'user',
        content: messageContent,
        timestamp: new Date(),
        metadata: options.metadata || {},
        files: options.files || []
      };

      setMessages(prev => [...prev, userMessage]);

      // Create assistant message placeholder
      const assistantMessageId = `assistant_${Date.now()}`;
      const assistantMessage = {
        id: assistantMessageId,
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        metadata: { isStreaming: true }
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Stream response
      let accumulatedContent = '';
      const response = await agentChatService.streamMessage(
        { message: messageContent, files: options.files, metadata: options.metadata },
        (chunk) => {
          accumulatedContent += chunk;
          // Update assistant message content with accumulated chunks
          setMessages(prev => prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, content: accumulatedContent }
              : msg
          ));

          // Call user's onChunk callback with individual chunk only
          if (onChunk) onChunk(chunk);
        }
      );

      // Mark streaming complete
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? { ...msg, metadata: { ...msg.metadata, isStreaming: false } }
          : msg
      ));

      return response;
    } catch (err) {
      console.error('Failed to stream message:', err);
      setError(err.message);
      if (onError) onError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, isSessionReady, onError]);

  // Switch to a different session
  const switchToSession = useCallback(async (newSessionId) => {
    try {
      setIsLoading(true);
      setError(null);

      agentChatService.setSessionId(newSessionId);
      setSessionId(newSessionId);

      // Load history for new session
      if (autoLoadHistory) {
        await loadSessionHistory(newSessionId);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to switch session:', err);
      setError(err.message);
      if (onError) onError(err);
    } finally {
      setIsLoading(false);
    }
  }, [autoLoadHistory, loadSessionHistory, onError]);

  // Get sessions list
  const getSessions = useCallback(async () => {
    try {
      return await agentChatService.getChatSessions();
    } catch (err) {
      console.error('Failed to get sessions:', err);
      if (onError) onError(err);
      throw err;
    }
  }, [onError]);

  return {
    // State
    sessionId,
    messages,
    isLoading,
    error,
    isSessionReady,

    // Actions
    sendMessage,
    streamMessage,
    newConversation,
    clearSession,
    loadSessionHistory,
    switchToSession,
    getSessions,

    // Utilities
    setError: (err) => setError(err?.message || err),
    clearError: () => setError(null),
    addMessage: (message) => setMessages(prev => [...prev, message]),
    updateMessage: (messageId, updates) => {
      setMessages(prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, ...updates } : msg
      ));
    }
  };
};

export default useChatSession;